import React, { useState } from 'react';
import { X, MessageSquare, Send, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FEEDBACK_TOPICS = [
  { value: 'will_creation', label: 'Will Creation' },
  { value: 'life_vault', label: 'Life Vault' },
  { value: 'payments_invoices', label: 'Payments & Invoices' },
  { value: 'profile_settings', label: 'Profile & Settings' },
  { value: 'general_feedback', label: 'General Feedback' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'bug_report', label: 'Bug Report' },
];

const FeedbackModal = ({ isOpen, onClose }) => {
  const [topic, setTopic] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const getAuthHeader = () => {
    const token = localStorage.getItem('clientToken');
    return { Authorization: `Bearer ${token}` };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!topic) {
      toast.error('Please select a topic');
      return;
    }
    
    if (!description.trim()) {
      toast.error('Please enter a description');
      return;
    }

    setSubmitting(true);
    
    try {
      await axios.post(
        `${API}/client/feedback`,
        { topic, description: description.trim() },
        { headers: getAuthHeader() }
      );
      
      toast.success('Thank you for your feedback!');
      setTopic('');
      setDescription('');
      onClose();
    } catch (error) {
      console.error('Feedback submission error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setTopic('');
      setDescription('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div 
        className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-amber-500/10">
              <MessageSquare className="w-5 h-5 text-amber-400" />
            </div>
            <h2 className="text-lg font-bold text-white">Share Your Feedback</h2>
          </div>
          <button
            onClick={handleClose}
            disabled={submitting}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5 space-y-5">
          {/* Topic Dropdown */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-300">
              Topic <span className="text-red-400">*</span>
            </label>
            <select
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              disabled={submitting}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition-all disabled:opacity-50"
              data-testid="feedback-topic"
            >
              <option value="">Select a topic</option>
              {FEEDBACK_TOPICS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-300">
              Description <span className="text-red-400">*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={submitting}
              placeholder="Tell us more about your feedback..."
              rows={5}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition-all resize-none disabled:opacity-50"
              data-testid="feedback-description"
            />
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold py-3 rounded-xl shadow-lg shadow-amber-500/25 disabled:opacity-50"
            data-testid="feedback-submit"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Submit Feedback
              </>
            )}
          </Button>
        </form>

        {/* Footer */}
        <div className="px-5 pb-5">
          <p className="text-xs text-slate-500 text-center">
            Your feedback helps us improve WillKart for everyone.
          </p>
        </div>
      </div>
    </div>
  );
};

export default FeedbackModal;
